<template>
  <Form @submit="onSubmit">
    <div class="flex border-b border-dark-b">
      <h2 class="text-xl md:text-2xl font-semibold md:mr-6 w-9/12 md:w-1/2">
        Products Sourced
        <span class="px-1 relative" 
          @mouseenter="showProductsSummaryDetails = true"
          @mouseleave="showProductsSummaryDetails = false"
        >
          ⓘ
          <div
            v-if="showProductsSummaryDetails"
            class="absolute z-20 lg2:-top-8 lg2:left-5 -right-40 pt-2 px-3"
          >
            <div class="px-2 py-2 w-60 sm:w-72 text-xs font-normal ml-2 border rounded-lg bg-gray-b">
              Please provide information on your previously sourced products. Kindly fill up the information for all the fields.
            </div>
          </div>
        </span>
        <span class="text-base font-normal">(Max 5)</span>
      </h2>
      <div class="flex lg:pl-2 text-xs items-center md:w-1/2 justify-end md:justify-between">
        <div>Progress</div>
        <div class="flex md:w-10/12 items-center justify-between">
          <div class="hidden md:inline w-1/5 h-2 ml-2 bg-green rounded-full"/>
          <div class="hidden md:inline w-4/5 h-2 bg-white rounded-r-full"/>
          <div class="pl-2 text-green md:text-black">20%</div>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full pt-7 md:pt-12">
      <div class="font-semibold text-lg lg:text-xl w-full">
        <div class="pb-2 flex items-center">
          Product Name
          <span class="px-2 relative" 
            @mouseenter="showProductNameDetails = true"
            @mouseleave="showProductNameDetails = false"
          >
            ⓘ
            <div
              v-if="showProductNameDetails"
              class="absolute z-20 lg2:-top-6 lg2:left-5 -right-16 pt-2 px-3"
            >
              <div class="px-2 py-2 w-60 sm:w-64 text-xs font-normal border rounded-lg bg-gray-b">
                Write Product Name for the products that you have sourced previously. e.g. Head Covers
              </div>
            </div>
          </span>
        </div>
        <Field
          name="productName"
          class="form-input text-sm lg:text-lg"
          placeholder="Type Product Name Here..."
          :rules="isRequired"
          v-model="formData.product_name"
        />
        <div class="h-7">
           <span
            v-if="!formData.product_name && productNameError"
            class="form-error"
          >
            This field is required.
          </span>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full">
      <div class="font-semibold text-lg lg:text-xl w-full">
        <div class="pb-2">
          Description of products sourced previously 
          <span class="px-1 relative" 
            @mouseenter="showProductsDescriptionDetails = true"
            @mouseleave="showProductsDescriptionDetails = false"
          >
            ⓘ
            <div
              v-if="showProductsDescriptionDetails"
              class="absolute z-20 lg2:-top-16 lg2:left-5 -right-16 pt-2 px-3"
            >
              <div class="px-2 py-2 w-60 sm:w-80 text-xs font-normal border rounded-lg bg-gray-b">
                Write a short description for the products that you have sourced previously. e.g. Hair Cover (Non- Sterile) widely used in food service, hospitals, laboratories, manufacturing etc. It is made of non-woven material, latex free, lightweight and breathable. Double Protection: Each cap has double elastic bands to keep the cap securely in place.
              </div>
            </div>
          </span>
        </div>
        <Field
          name="productDescription"
          type="textarea"
          :rules="isRequired"
          v-model="formData.description"
          v-slot="{field}"
        >
          <textarea 
            v-bind="field"
            class="border border-dark-b text-sm lg:text-lg px-3 py-3 w-full rounded-lg bg-white h-52 max-h-52 lg:h-40 lg:max-h-40" 
            placeholder="Type Description here..." 
          />
        </Field>
        <div class="h-7">
          <span
            v-if="!formData.description && descriptionError"
            class="form-error"
          >
            This field is required.
          </span>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap w-full">
      <div class="lg:pr-2 w-full lg:w-1/2 font-semibold text-lg lg:text-xl">
        <div class="pb-2">
          Images of product sourced previously 
          <span class="px-1 relative" 
            @mouseenter="showProductsImageDescriptionDetails = true"
            @mouseleave="showProductsImageDescriptionDetails = false"
          >
            ⓘ
            <div
              v-if="showProductsImageDescriptionDetails"
              class="absolute z-20 lg2:-top-4 lg2:left-5 -right-32 pt-2 px-3"
            >
              <div class="px-2 py-2 w-60 sm:w-80 text-xs font-normal border rounded-lg bg-gray-b">
                Upload an image of the product sourced previously.
              </div>
            </div>
          </span>
        </div>
        <div class="form-input relative flex items-center">
          <Field
            name="productImage"
            class="absolute inset-0 opacity-0 z-10 w-full overflow-hidden"
            type="file"
            :rules="fileRequired"
            v-model="formData.product_image"
          />
          <span
            v-if="formData.product_image && formData.product_image[0]"
            class="pointer-events-none absolute z-10 left-0 pl-4 w-10/12 truncate text-sm lg:text-lg font-normal"
          >
            {{ formData.product_image[0].name }}
          </span>
          <span
            v-else
            class="pointer-events-none absolute font-normal text-sm lg:text-lg z-10 left-0 px-4 opacity-50 tracking-tighter md:tracking-normal"
          >
            Jpeg or Png Max File Size 2MB
          </span>
          <img src="@/assets/icons/upload.svg" class="ml-auto w-4-5 h-4-5">
        </div>
        <div class="h-3 lg:h-5">
          <span
            v-if="!(formData.product_image && formData.product_image[0]) && productImageError"
            class="form-error"
          >
            This field is required.
          </span>
        </div>
      </div>
    </div>
    <div class="flex justify-end pt-2 pb-2">
      <button
        class="bg-blue text-white py-3 px-8 text-lg lg:text-xl rounded-full font-bold"
        :disable="productImages.length > 4"
        :class="{'opacity-50 bg-gray-550': productImages.length > 4}"
        @click="validateFields"         
      >
        Save
      </button>
    </div>
    <div class="flex flex-wrap w-full" :class="{'pt-14' : productImages.length}">
      <div
        v-for="(image, index) in productImages"
        :key="`product-${index}`"
        class="w-full lg:w-1/2 font-semibold text-xl pb-5 md:pb-10"
        :class="{ 'lg:pr-10' : index%2 === 0}"
      >
        <div class="px-2 py-1 text-opacity-50 font-normal text-xs">
          <div class="flex justify-between">
            <div class="flex justify-between">
              <img :src="image.thumbnail" class="w-22 md:w-40 h-16 md:h-28"/>
              <div class="px-4 w-full">
                <div class="text-sm lg:text-base font-semibold">
                  {{ image.name }}
                </div>
                <div class="text-sm lg:text-base font-normal break-all"> 
                  {{ image.description }}
                </div>
              </div>
            </div>
            <div
              class="cursor-pointer text-lg"
              @click="removeProduct(index)"
            >x</div>
          </div>
        </div>
      </div>
    </div>
  </Form>
</template>
<script>
import { mapActions } from 'vuex'
import VeeValidate from '@/shared/mixins/VeeValidate'

export default {
  name: 'ProductsSourced',
  mixins: [VeeValidate],
  data() {
    return {
      formData: {},
      showProductsSummaryDetails: false,
      showProductNameDetails: false,
      showProductsDescriptionDetails: false,
      showProductsImageDescriptionDetails: false,
      productNameError: false,
      descriptionError: false,
      productImageError: false,
      productImages: []
    }
  }, 
  computed: {
    isCustomError() {
      return this.productNameError || this.descriptionError || this.productImageError
    }
  },
  methods: {
    ...mapActions([
      'addSourcedProduct'
    ]),
    validateFields() {
      this.productNameError = !this.formData.product_name
      this.descriptionError = !this.formData.description
      this.productImageError = !(this.formData.product_image && this.formData.product_image[0])
    },
    removeProduct(index) {
      this.productImages.splice(index, 1)
    },
    async onSubmit() {
      if (this.productImages.length > 4) return
      if (this.isCustomError) return
      const response = await this.addSourcedProduct(this.formatFormData())
      this.$notify({
        group: 'buyHive',
        type: response.result ? 'success' : 'error',
        text: response.message
      })
      this.productImages.push({ 'name': this.formData.product_name, 'description': this.formData.description , 'thumbnail': URL.createObjectURL(this.formData.product_image[0]) })
      this.formData = {}
      this.productNameError = false
      this.descriptionError = false
      this.productImageError = false
     },
    formatFormData() {
      const userData = new FormData()
      Object.keys(this.formData).forEach(key => {
        if (key !== 'product_image') userData.append(key, this.formData[key] || '')
      })
      userData.append('product_image', this.formData.product_image[0])
      return userData
    }
  }
}
</script>
